// src/services/sharedCollectionService.ts
import {
   collection,
   doc,
   addDoc,
   updateDoc,
   getDoc,
   getDocs,
   query,
   where,
   orderBy,
   serverTimestamp,
   writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getAuthHeaders, getAuthToken } from '../utils/auth';
import {
   SharedCollection,
   SharedCollectionRequest,
   SharedCollectionResponse,
   SharedRecipient,
   SharedPhoto,
} from '../types/SharedCollection';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4001';

export class SharedCollectionService {
   /**
    * Create a shared collection and send invitations
    */
   static async createSharedCollection(
      request: SharedCollectionRequest
   ): Promise<SharedCollectionResponse> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${API_BASE_URL}/api/shared-collections/create`, {
            method: 'POST',
            headers,
            body: JSON.stringify(request),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to create shared collection');
         }

         return await response.json();
      } catch (error) {
         console.error('Error creating shared collection:', error);
         throw error;
      }
   }

   /**
    * Get shared collections created by current user
    */
   static async getUserSharedCollections(userId: string): Promise<SharedCollection[]> {
      try {
         const q = query(
            collection(db, 'sharedCollections'),
            where('createdBy', '==', userId),
            orderBy('createdAt', 'desc')
         );

         const snapshot = await getDocs(q);
         const collections: SharedCollection[] = [];

         for (const docSnapshot of snapshot.docs) {
            const data = docSnapshot.data();
            // Safely convert dates
            const createdAt = data.createdAt?.toDate
               ? data.createdAt.toDate()
               : data.createdAt instanceof Date
                 ? data.createdAt
                 : new Date();
            const updatedAt = data.updatedAt?.toDate
               ? data.updatedAt.toDate()
               : data.updatedAt instanceof Date
                 ? data.updatedAt
                 : new Date();

            // Refresh profile photos for recipients if they're missing
            const refreshedSharedWith = await Promise.all(
               (data.sharedWith || []).map(async (recipient: any) => {
                  // If photoURL is empty or missing, try to fetch from backend
                  if (!recipient.photoURL) {
                     try {
                        const headers = await getAuthHeaders();
                        const response = await fetch(
                           `${API_BASE_URL}/api/users/profile/${encodeURIComponent(recipient.email)}`,
                           {
                              headers,
                           }
                        );
                        if (response.ok) {
                           const profileData = await response.json();
                           return {
                              ...recipient,
                              displayName: profileData.displayName || recipient.displayName,
                              photoURL: profileData.photoURL || recipient.photoURL,
                           };
                        }
                     } catch (error) {
                        console.log(`Failed to refresh profile for ${recipient.email}`);
                     }
                  }
                  // Convert date fields from Firestore timestamps to Date objects
                  const convertedRecipient = {
                     ...recipient,
                     dateSent: recipient.dateSent?.toDate
                        ? recipient.dateSent.toDate()
                        : recipient.dateSent instanceof Date
                          ? recipient.dateSent
                          : recipient.invitedAt?.toDate
                            ? recipient.invitedAt.toDate() // Fallback to legacy field
                            : recipient.invitedAt instanceof Date
                              ? recipient.invitedAt
                              : new Date(),
                     viewedAt: recipient.viewedAt?.toDate
                        ? recipient.viewedAt.toDate()
                        : recipient.viewedAt instanceof Date
                          ? recipient.viewedAt
                          : recipient.viewedAt
                            ? new Date(recipient.viewedAt)
                            : undefined,
                     joinedAt: recipient.joinedAt?.toDate
                        ? recipient.joinedAt.toDate()
                        : recipient.joinedAt instanceof Date
                          ? recipient.joinedAt
                          : recipient.joinedAt
                            ? new Date(recipient.joinedAt)
                            : undefined,
                  };
                  return convertedRecipient;
               })
            );

            collections.push({
               id: docSnapshot.id,
               ...data,
               createdAt,
               updatedAt,
               sharedWith: refreshedSharedWith,
            } as SharedCollection);
         }

         return collections;
      } catch (error) {
         console.error('Error fetching user shared collections:', error);
         throw error;
      }
   }

   /**
    * Resend collection to additional or existing recipients
    */
   static async resendCollection(
      collectionId: string,
      recipients: { participants: string[]; nonParticipants: string[] }
   ): Promise<{ success: boolean; message: string; emailsSent?: any; errors?: string[] }> {
      try {
         const token = await getAuthToken();
         const response = await fetch(
            `${API_BASE_URL}/api/shared-collections/${collectionId}/resend`,
            {
               method: 'POST',
               headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${token}`,
               },
               body: JSON.stringify({ recipients }),
            }
         );

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(
               errorData.error || `HTTP ${response.status}: Failed to resend collection`
            );
         }

         return await response.json();
      } catch (error) {
         console.error('Error resending collection:', error);
         throw error;
      }
   }

   /**
    * Share existing collection with new recipients (simple email list)
    */
   static async shareExistingCollection(
      collectionId: string,
      emailList: string[],
      message?: string,
      subject?: string,
      senderName?: string
   ): Promise<{ success: boolean; message: string; emailsSent?: string[]; errors?: string[] }> {
      try {
         const token = await getAuthToken();
         const response = await fetch(`${API_BASE_URL}/api/shared-collections/share-existing`, {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
               collectionId,
               emailList,
               message,
               subject,
               senderName,
            }),
         });

         if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            throw new Error(
               errorData.error || `HTTP ${response.status}: Failed to share collection`
            );
         }

         return await response.json();
      } catch (error) {
         console.error('Error sharing existing collection:', error);
         throw error;
      }
   }

   /**
    * Get shared collections by share token (for public access)
    */
   static async getSharedCollectionByToken(
      shareToken: string,
      viewerEmail?: string
   ): Promise<SharedCollection | null> {
      try {
         // Use backend API instead of direct Firestore to handle view tracking properly
         const headers = {
            'Content-Type': 'application/json',
         };

         let url = `${API_BASE_URL}/api/shared-collections/public/${shareToken}`;
         if (viewerEmail) {
            url += `?viewerEmail=${encodeURIComponent(viewerEmail)}`;
         }

         console.log('Fetching shared collection from URL:', url);

         const response = await fetch(url, {
            method: 'GET',
            headers,
         });

         console.log('Response status:', response.status, response.statusText);

         if (!response.ok) {
            if (response.status === 404) {
               console.log('Collection not found (404) for token:', shareToken);
               return null;
            }
            const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
            console.error('API error:', response.status, errorData);
            throw new Error(
               errorData.error || `HTTP ${response.status}: Failed to fetch shared collection`
            );
         }

         const responseData = await response.json();

         if (!responseData.success || !responseData.collection) {
            return null;
         }

         const data = responseData.collection;
         return {
            id: data.id,
            ...data,
            createdAt: data.createdAt ? new Date(data.createdAt) : new Date(),
            updatedAt: data.updatedAt ? new Date(data.updatedAt) : new Date(),
            viewersList: (data.viewersList || []).map((viewer: any) => ({
               ...viewer,
               viewedAt: viewer.viewedAt ? new Date(viewer.viewedAt) : new Date(),
            })),
         } as SharedCollection;
      } catch (error) {
         console.error('Error fetching shared collection by token:', error);
         throw error;
      }
   }

   /**
    * Update recipient status when they view or join
    */
   static async updateRecipientStatus(
      collectionId: string,
      recipientEmail: string,
      status: 'viewed',
      userId?: string
   ): Promise<void> {
      try {
         const docRef = doc(db, 'sharedCollections', collectionId);
         const docSnapshot = await getDoc(docRef);

         if (!docSnapshot.exists()) {
            throw new Error('Shared collection not found');
         }

         const data = docSnapshot.data();
         const sharedWith = data.sharedWith || [];

         // Find and update the recipient
         const updatedSharedWith = sharedWith.map((recipient: SharedRecipient) => {
            if (recipient.email === recipientEmail) {
               const updates: Partial<SharedRecipient> = { status };

               if (status === 'viewed' && !recipient.viewedAt) {
                  updates.viewedAt = new Date();
               }

               return { ...recipient, ...updates };
            }
            return recipient;
         });

         await updateDoc(docRef, {
            sharedWith: updatedSharedWith,
            updatedAt: serverTimestamp(),
         });
      } catch (error) {
         console.error('Error updating recipient status:', error);
         throw error;
      }
   }

   /**
    * Delete a shared collection
    */
   static async deleteSharedCollection(collectionId: string, userId: string): Promise<void> {
      try {
         const docRef = doc(db, 'sharedCollections', collectionId);
         const docSnapshot = await getDoc(docRef);

         if (!docSnapshot.exists()) {
            throw new Error('Shared collection not found');
         }

         const data = docSnapshot.data();
         if (data.createdBy !== userId) {
            throw new Error('You do not have permission to delete this collection');
         }

         const headers = await getAuthHeaders();
         const response = await fetch(`${API_BASE_URL}/api/shared-collections/${collectionId}`, {
            method: 'DELETE',
            headers,
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete shared collection');
         }
      } catch (error) {
         console.error('Error deleting shared collection:', error);
         throw error;
      }
   }

   /**
    * Get analytics for a shared collection
    */
   static async getCollectionAnalytics(collectionId: string): Promise<{
      viewCount: number;
      recipientStats: {
         total: number;
         viewed: number;
         pending: number;
      };
      topViewers: Array<{ email: string; displayName?: string; viewedAt: Date }>;
      allViewers: Array<{
         email: string;
         displayName?: string;
         isRegistered: boolean;
         viewedAt: Date;
      }>;
   }> {
      try {
         const docRef = doc(db, 'sharedCollections', collectionId);
         const docSnapshot = await getDoc(docRef);

         if (!docSnapshot.exists()) {
            throw new Error('Shared collection not found');
         }

         const data = docSnapshot.data();
         const sharedWith: SharedRecipient[] = data.sharedWith || [];
         const viewersList: any[] = data.viewersList || [];

         const recipientStats = {
            total: sharedWith.length,
            viewed: sharedWith.filter((r) => r.status === 'viewed').length,
            pending: sharedWith.filter((r) => r.status === 'unviewed').length,
         };

         // Get top viewers from recipients (legacy)
         const topViewersFromRecipients = sharedWith
            .filter((r) => r.viewedAt)
            .sort((a, b) => (b.viewedAt?.getTime() || 0) - (a.viewedAt?.getTime() || 0))
            .slice(0, 5)
            .map((r) => ({
               email: r.email,
               displayName: r.displayName,
               viewedAt: r.viewedAt!,
            }));

         // Get all viewers from new viewersList
         const allViewers = viewersList
            .map((viewer) => ({
               email: viewer.email,
               displayName: viewer.displayName,
               isRegistered: viewer.isRegistered,
               viewedAt: viewer.viewedAt?.toDate
                  ? viewer.viewedAt.toDate()
                  : new Date(viewer.viewedAt),
            }))
            .sort((a, b) => b.viewedAt.getTime() - a.viewedAt.getTime());

         // Use viewersList for topViewers if available, otherwise fall back to recipients
         const topViewers =
            allViewers.length > 0 ? allViewers.slice(0, 5) : topViewersFromRecipients;

         return {
            viewCount: data.viewCount || 0,
            recipientStats,
            topViewers,
            allViewers,
         };
      } catch (error) {
         console.error('Error getting collection analytics:', error);
         throw error;
      }
   }

   /**
    * Get likes analytics for a shared collection
    */
   static async getCollectionLikesAnalytics(collectionId: string): Promise<{
      totalLikes: number;
      mostLikedPhotos: Array<{
         id: string;
         title: string;
         url: string;
         thumbnailUrl: string;
         contextualLikes: number;
         totalLikes: number;
         uploadedBy: string;
         uploadedAt: Date;
      }>;
      likesPerPhoto: Record<string, any>;
      photoCount: number;
      averageLikesPerPhoto: number;
   }> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/shared-collections/${collectionId}/likes-analytics`,
            {
               method: 'GET',
               headers,
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch likes analytics');
         }

         const data = await response.json();
         return data.analytics;
      } catch (error) {
         console.error('Error fetching collection likes analytics:', error);
         throw error;
      }
   }

   /**
    * Get collections shared with the current user
    */
   static async getReceivedCollections(): Promise<SharedCollection[]> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(`${API_BASE_URL}/api/shared-collections/received`, {
            method: 'GET',
            headers,
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to fetch received collections');
         }

         const data = await response.json();
         return data.collections || [];
      } catch (error) {
         console.error('Error fetching received collections:', error);
         throw error;
      }
   }

   /**
    * Delete a shared collection for current user only (remove from their view)
    */
   static async deleteCollectionForUser(
      collectionId: string
   ): Promise<{ success: boolean; message: string }> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/shared-collections/${collectionId}/delete-for-user`,
            {
               method: 'POST',
               headers,
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to delete collection');
         }

         return await response.json();
      } catch (error) {
         console.error('Error deleting collection for user:', error);
         throw error;
      }
   }

   /**
    * Update collection title
    */
   static async updateCollectionTitle(collectionId: string, title: string): Promise<void> {
      try {
         const headers = await getAuthHeaders();
         const response = await fetch(
            `${API_BASE_URL}/api/shared-collections/${collectionId}/title`,
            {
               method: 'PATCH',
               headers,
               body: JSON.stringify({ title }),
            }
         );

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to update collection title');
         }
      } catch (error) {
         console.error('Error updating collection title:', error);
         throw error;
      }
   }
}

export default SharedCollectionService;
